/**
 * Terminology Settings Component
 *
 * Allows users to customize terminology used throughout the app.
 * E.g., "Work Package" → "Project", "Task", "Review", etc.
 */

import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Space, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface TerminologySettings {
  workPackageName: string;
  workPackageNamePlural: string;
}

const DEFAULT_TERMINOLOGY: TerminologySettings = {
  workPackageName: 'Work Package',
  workPackageNamePlural: 'Work Packages',
};

export default function TerminologySettings() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [terminology, setTerminology] = useState<TerminologySettings>(DEFAULT_TERMINOLOGY);

  // Load from localStorage for now (will be backend-stored later)
  useEffect(() => {
    const stored = localStorage.getItem('terminology');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setTerminology(parsed);
        form.setFieldsValue(parsed);
      } catch (e) {
        console.error('Failed to parse terminology settings', e);
      }
    } else {
      form.setFieldsValue(DEFAULT_TERMINOLOGY);
    }
  }, [form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Save to localStorage (will be API call later)
      localStorage.setItem('terminology', JSON.stringify(values));
      setTerminology(values);

      message.success('Terminology updated - refresh to see changes');

      // Trigger page reload after short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_TERMINOLOGY);
    localStorage.removeItem('terminology');
    setTerminology(DEFAULT_TERMINOLOGY);
    message.success('Reset to defaults - refresh to see changes');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <Card
      title="Terminology"
      extra={
        <Space>
          <Button onClick={handleReset} size="small">
            Reset
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
            size="small"
          >
            Save
          </Button>
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Customize how work items are named throughout the application.
      </Text>

      <Form form={form} layout="vertical">
        <Form.Item
          label="Work Item Name (Singular)"
          name="workPackageName"
          rules={[{ required: true, message: 'Required' }]}
          extra="Examples: Project, Task, Review, Initiative"
        >
          <Input placeholder="Work Package" />
        </Form.Item>

        <Form.Item
          label="Work Item Name (Plural)"
          name="workPackageNamePlural"
          rules={[{ required: true, message: 'Required' }]}
          extra="Examples: Projects, Tasks, Reviews, Initiatives"
        >
          <Input placeholder="Work Packages" />
        </Form.Item>
      </Form>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          background: '#f5f5f5',
          borderRadius: 6,
          fontSize: 12,
        }}
      >
        <Text type="secondary">
          <strong>Preview:</strong> "Create new {terminology.workPackageName}" · "View all{' '}
          {terminology.workPackageNamePlural}" · "{terminology.workPackageName} details"
        </Text>
      </div>
    </Card>
  );
}

// Export helper to get current terminology
export function getTerminology(): TerminologySettings {
  const stored = localStorage.getItem('terminology');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return DEFAULT_TERMINOLOGY;
    }
  }
  return DEFAULT_TERMINOLOGY;
}
